import {
  AssigneePills,
  GlassBadge,
  GlassCard,
  GlassCardHeader,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
  SERVICE_ORDER_STATUS_LABEL,
  SERVICE_ORDER_STATUS_VARIANT,
  SkeletonLoader,
} from '@merritrac/shared/components';
import { useNavigate } from 'react-router';

import type { ServiceOrderStatus } from '@merritrac/shared/components';
import type { ReactElement } from 'react';

interface OrderRow {
  accessKey: number;
  id: string;
  client: string;
  location: string;
  status: ServiceOrderStatus;
  assignee: string;
  date: string;
}

interface RecentOrdersTableProps {
  orders: OrderRow[];
  loading?: boolean;
  error?: string | undefined;
}

export function RecentOrdersTable({
  orders,
  loading,
  error,
}: RecentOrdersTableProps): ReactElement {
  const navigate = useNavigate();

  if (loading === true) {
    return (
      <GlassCard>
        <GlassCardHeader>
          <SkeletonLoader variant="text" width="200px" />
        </GlassCardHeader>
        <div className="p-4 space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonLoader key={i} variant="block" height="40px" />
          ))}
        </div>
      </GlassCard>
    );
  }

  if (error !== undefined && error !== '') {
    return (
      <GlassCard>
        <div className="py-12 text-center text-sm text-danger">Failed to load orders: {error}</div>
      </GlassCard>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-text">Recent Service Orders</h2>
        <GlassBadge variant="info">{orders.length} orders</GlassBadge>
      </div>
      <GlassTable>
        <GlassTableHead>
          <GlassTableRow>
            <GlassTableHeaderCell>Order</GlassTableHeaderCell>
            <GlassTableHeaderCell>Client</GlassTableHeaderCell>
            <GlassTableHeaderCell>Location</GlassTableHeaderCell>
            <GlassTableHeaderCell>Status</GlassTableHeaderCell>
            <GlassTableHeaderCell>Assignee</GlassTableHeaderCell>
            <GlassTableHeaderCell>Date</GlassTableHeaderCell>
          </GlassTableRow>
        </GlassTableHead>
        <GlassTableBody>
          {orders.map((order) => (
            <GlassTableRow
              key={order.accessKey}
              className="cursor-pointer"
              onClick={() => {
                void navigate(`/service-orders/${String(order.accessKey)}`);
              }}
            >
              <GlassTableCell className="font-medium text-primary">{order.id}</GlassTableCell>
              <GlassTableCell>{order.client}</GlassTableCell>
              <GlassTableCell className="text-text-secondary">{order.location}</GlassTableCell>
              <GlassTableCell>
                <GlassBadge variant={SERVICE_ORDER_STATUS_VARIANT[order.status]} dot>
                  {SERVICE_ORDER_STATUS_LABEL[order.status]}
                </GlassBadge>
              </GlassTableCell>
              <GlassTableCell>
                <AssigneePills names={order.assignee} />
              </GlassTableCell>
              <GlassTableCell className="text-text-tertiary">{order.date}</GlassTableCell>
            </GlassTableRow>
          ))}
          {orders.length === 0 && (
            <GlassTableRow>
              <GlassTableCell colSpan={6} className="text-center py-10 text-text-tertiary">
                No service orders found.
              </GlassTableCell>
            </GlassTableRow>
          )}
        </GlassTableBody>
      </GlassTable>
    </div>
  );
}
